'use client';

import { c as _c } from "react/compiler-runtime";
import './cart-drawer.scss';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert, type AlertProps } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Drawer, DrawerActions, DrawerBody, DrawerClose, DrawerContent, DrawerFooter, type DrawerProps, DrawerTrigger } from '@sola/ark-core/molecules/drawer/drawer.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { useCart } from '@sola/cart-sdk/store.tsx';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';
import { BirthdayAppealCartItem } from '../birthday-appeal/birthday-appeal-cart-item.tsx';
import { CartItem as CartItemComponent } from '../cart-item/cart-item.tsx';
import { MiniGallery } from '../mini-gallery/mini-gallery.tsx';
const cartDrawerFocusTargetAttribute = 'data-cart-drawer-focus-target';
export const cartDrawerFocusTargetProps = {
  [cartDrawerFocusTargetAttribute]: true
} as const;
const restoreCartDrawerFocus = (event: Pick<Event, 'preventDefault'>) => {
  const returnFocusTarget = globalThis.document.querySelector<HTMLElement>(`[${cartDrawerFocusTargetAttribute}]`);
  if (!returnFocusTarget?.isConnected) {
    return;
  }
  returnFocusTarget.focus();
  if (returnFocusTarget.ownerDocument.activeElement === returnFocusTarget) {
    event.preventDefault();
  }
};
export interface CartDrawerAlert {
  message: AlertProps['message'];
  variant?: AlertProps['variant'];
  title?: AlertProps['title'];
}
type CartDrawerProps = Omit<DrawerProps, 'children'> & {
  trigger?: ReactElement;
  alert?: CartDrawerAlert | null;
  onAlertClose?: () => void;
};
interface CartDrawerBodyProps {
  cartItems: CartItem[] | undefined;
  hasError: boolean;
  isReady: boolean;
}
interface CartDrawerSubtitleProps {
  isReady: boolean;
  itemCount: number;
  totalToday: number;
}
interface CartDrawerListProps {
  cartItems: CartItem[];
}
const CartDrawerEmpty: React.FC = () => {
  const $ = _c(56);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["cart", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let T0;
  let T1;
  let T2;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== t) {
    const {
      NEXT_PUBLIC_BASE_URL_DOTCOM: dotcomBaseUrl
    } = getPublicConfig();
    t12 = "CartDrawer__empty py-comfy-lg overflow-hidden";
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <MiniGallery className="pl-comfy-sm md:pl-comfy-xl" />;
      $[19] = t13;
    } else {
      t13 = $[19];
    }
    t9 = "px-comfy-sm md:px-comfy-xl";
    const t15 = t("cart:emptyCart.title");
    if ($[20] !== t15) {
      t10 = <Text asChild={true} variant={Variants.Heading} size={Sizes.XSmall}><p className="mt-comfy-xl">{t15}</p></Text>;
      $[20] = t15;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    const t16 = t("cart:emptyCart.subTitle");
    if ($[22] !== t16) {
      t11 = <p>{t16}</p>;
      $[22] = t16;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    t7 = "mt-comfy-xl flex flex-col gap-comfy-sm md:flex-row";
    let t17;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <ArkIcon iconName="arrow-right" />;
      $[24] = t17;
    } else {
      t17 = $[24];
    }
    const t18 = <ButtonStyle variant="primary" size="md" iconAfter={t17} fullWidth={true}><Link href={new URL(knownSkippyUrls.sac, dotcomBaseUrl)}>{t("core:actions.sponsorAChild")}</Link></ButtonStyle>;
    if ($[25] !== t18) {
      t8 = <DrawerClose asChild={true}>{t18}</DrawerClose>;
      $[25] = t18;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    T2 = DrawerClose;
    t6 = true;
    T1 = ButtonStyle;
    t2 = "secondary";
    t3 = "md";
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ArkIcon iconName="arrow-right" />;
      $[27] = t4;
    } else {
      t4 = $[27];
    }
    t5 = true;
    T0 = Link;
    t1 = new URL(knownSkippyUrls.give, dotcomBaseUrl);
    t14 = t("core:actions.donate");
    $[1] = t;
    $[2] = T0;
    $[3] = T1;
    $[4] = T2;
    $[5] = t1;
    $[6] = t10;
    $[7] = t11;
    $[8] = t12;
    $[9] = t13;
    $[10] = t14;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    T0 = $[2];
    T1 = $[3];
    T2 = $[4];
    t1 = $[5];
    t10 = $[6];
    t11 = $[7];
    t12 = $[8];
    t13 = $[9];
    t14 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t15;
  if ($[28] !== T0 || $[29] !== t1 || $[30] !== t14) {
    t15 = <T0 href={t1}>{t14}</T0>;
    $[28] = T0;
    $[29] = t1;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== T1 || $[33] !== t15 || $[34] !== t2 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5) {
    t16 = <T1 variant={t2} size={t3} iconAfter={t4} fullWidth={t5}>{t15}</T1>;
    $[32] = T1;
    $[33] = t15;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== T2 || $[40] !== t16 || $[41] !== t6) {
    t17 = <T2 asChild={t6}>{t16}</T2>;
    $[39] = T2;
    $[40] = t16;
    $[41] = t6;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17 || $[44] !== t7 || $[45] !== t8) {
    t18 = <div className={t7}>{t8}{t17}</div>;
    $[43] = t17;
    $[44] = t7;
    $[45] = t8;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t10 || $[48] !== t11 || $[49] !== t18 || $[50] !== t9) {
    t19 = <div className={t9}>{t10}{t11}{t18}</div>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t18;
    $[50] = t9;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t12 || $[53] !== t13 || $[54] !== t19) {
    t20 = <div className={t12}>{t13}{t19}</div>;
    $[52] = t12;
    $[53] = t13;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
};
const CartDrawerList: React.FC<CartDrawerListProps> = t0 => {
  const $ = _c(4);
  const {
    cartItems
  } = t0;
  let t1;
  if ($[0] !== cartItems) {
    t1 = cartItems.map(_temp);
    $[0] = cartItems;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="CartDrawer__list px-comfy-sm md:px-comfy-xl pb-comfy-lg">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const CartDrawerBody: React.FC<CartDrawerBodyProps> = t0 => {
  const $ = _c(5);
  const {
    cartItems,
    hasError,
    isReady
  } = t0;
  if (!isReady) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="CartDrawer__loading my-comfy-sm px-comfy-sm md:px-comfy-xl"><Text asChild={true} variant={Variants.Label}><Skeleton count={3} height="4rem" className="my-snug-xl" /></Text></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (hasError) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="px-comfy-sm md:px-comfy-xl"><Alert className="my-comfy-md" variant="negative" message={<IntlBlock i18nKey="cart:errors.generic" components={{
          anchor: <a />
        }} />} /></div>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (!cartItems?.length) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CartDrawerEmpty />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== cartItems) {
    t1 = <CartDrawerList cartItems={cartItems} />;
    $[3] = cartItems;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const CartDrawerSubtitle: React.FC<CartDrawerSubtitleProps> = t0 => {
  const $ = _c(13);
  const {
    isReady,
    itemCount,
    totalToday
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["cart", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  if (!isReady) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Skeleton height="1.125rem" width="7.5rem" />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] !== t || $[3] !== totalToday) {
    t2 = t("core:numbers.currencyCustom", {
      amount: totalToday
    });
    $[2] = t;
    $[3] = totalToday;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== itemCount || $[6] !== t) {
    t3 = t("cart:drawer.itemCount", {
      count: itemCount
    });
    $[5] = itemCount;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <Text asChild={true} variant={Variants.Label}><span className="label-neutral-mild">{t3}</span></Text>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <>{t2}{" "}{t4}</>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
export const CartDrawer: React.FC<CartDrawerProps> = t0 => {
  const $ = _c(49);
  let alert;
  let drawerProps;
  let onAlertClose;
  let trigger;
  if ($[0] !== t0) {
    ({
      trigger,
      alert,
      onAlertClose,
      ...drawerProps
    } = t0);
    $[0] = t0;
    $[1] = alert;
    $[2] = drawerProps;
    $[3] = onAlertClose;
    $[4] = trigger;
  } else {
    alert = $[1];
    drawerProps = $[2];
    onAlertClose = $[3];
    trigger = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["cart", "core"];
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    actions: t2,
    cart,
    errors,
    isReady
  } = useCart("actions", "cart", "errors", "isReady");
  const {
    goToCheckout
  } = t2;
  const itemCount = cart?.items?.length ?? 0;
  const totalToday = cart?.totalToday ?? 0;
  const error = errors?.[0];
  let t3;
  if ($[6] !== trigger) {
    t3 = trigger && <DrawerTrigger>{trigger}</DrawerTrigger>;
    $[6] = trigger;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = trigger ? undefined : restoreCartDrawerFocus;
  let t5;
  if ($[8] !== t) {
    t5 = t("cart:drawer.title");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isReady || $[11] !== itemCount || $[12] !== totalToday) {
    t6 = <CartDrawerSubtitle isReady={isReady} itemCount={itemCount} totalToday={totalToday} />;
    $[10] = isReady;
    $[11] = itemCount;
    $[12] = totalToday;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== alert || $[15] !== onAlertClose) {
    t7 = alert && <div className="px-comfy-sm md:px-comfy-xl"><Alert className="my-comfy-md" variant={alert.variant ?? "positive"} title={alert.title} message={alert.message} closable={true} onClose={onAlertClose} /></div>;
    $[14] = alert;
    $[15] = onAlertClose;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = cart?.items;
  const t9 = !!error;
  let t10;
  if ($[17] !== isReady || $[18] !== t8 || $[19] !== t9) {
    t10 = <CartDrawerBody cartItems={t8} hasError={t9} isReady={isReady} />;
    $[17] = isReady;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t7) {
    t11 = <DrawerBody>{t7}{t10}</DrawerBody>;
    $[21] = t10;
    $[22] = t7;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t) {
    t12 = t("core:actions.close");
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12) {
    t13 = <DrawerClose asChild={true}><ButtonStyle fullWidth={true} purpose="neutral" variant="secondary"><button type="button">{t12}</button></ButtonStyle></DrawerClose>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = !cart?.items?.length;
  let t15;
  if ($[28] !== t) {
    t15 = t("cart:drawer.checkoutLabel");
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== goToCheckout || $[31] !== t15) {
    t16 = <button onClick={goToCheckout}>{t15}</button>;
    $[30] = goToCheckout;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t14 || $[34] !== t16) {
    t17 = <ButtonStyle fullWidth={true} disabled={t14}>{t16}</ButtonStyle>;
    $[33] = t14;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t13 || $[37] !== t17) {
    t18 = <DrawerFooter><DrawerActions>{t13}{t17}</DrawerActions></DrawerFooter>;
    $[36] = t13;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t11 || $[40] !== t18 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6) {
    t19 = <DrawerContent className="CartDrawer__content" onCloseAutoFocus={t4} title={t5} subtitle={t6}>{t11}{t18}</DrawerContent>;
    $[39] = t11;
    $[40] = t18;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== drawerProps || $[46] !== t19 || $[47] !== t3) {
    t20 = <Drawer {...drawerProps}>{t3}{t19}</Drawer>;
    $[45] = drawerProps;
    $[46] = t19;
    $[47] = t3;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
};
function _temp(item) {
  return <Fragment key={item.id}><div className="CartDrawer__item my-comfy-md"><CartItemComponent item={item} /></div><BirthdayAppealCartItem item={item} className="CartDrawer__birthday-appeal" background="var(--color-container-background-neutral-mild, #f6f7f9)" flush={true} /><Divider purpose="neutral" /></Fragment>;
}